import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { addAssetAppealRespondBack } from "services/assetAppealService";
import { createFeedback } from "services/assetRecoveryService";
import { Roles } from "types";
import { hasRole } from "utils/helper";

interface Feedback {
  id: number;
  research_request: number;
  given_by: string;
  role: string;
  feedback_type: string;
  note: string;
  status: string;

  // add any other fields your API returns
}

function FeedBackForm({
  request,
  onSubmit,
  type,
  referralId,
  appealId,
}: {
  type?: any;
  request: any;
  onSubmit: (response: any) => void;
  referralId?: string;
  appealId?: string;
}) {
  const [responseText, setResponseText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { showTooltip } = useTooltip();
  const { t } = useTranslation();
  const { loginData } = useAuth();

  const handleSubmit = async () => {
    if (!responseText.trim()) return; // prevent empty submissions
    const data = new FormData();

    const userId = loginData?.user_id ?? null;
    const username = loginData?.full_name ?? null;
    const isAssetCoordinator = hasRole(loginData?.roles, [
      Roles.assetCoordinator,
    ]);

    // Create FormData for multipart upload

    // Append text fields
    if (type === "appeal") {
      data.append("asset_appeal_request", appealId as string);
    } else if (type === "appeal-respondent") {
      data.append("asset_respondent_appeal", appealId as string);
    } else {
      data.append("referral", referralId as string);
    }
    data.append("given_by_user_id", userId?.toString() || "");
    data.append("given_by_name", username || "");
    data.append("role", isAssetCoordinator ? "coordinator" : "prosecutor");
    data.append("feedback_type", "normal");
    data.append("feedback_summary", responseText);
    data.append("feedback_date", new Date().toISOString().split("T")[0]);

    try {
      setSubmitting(true);

      let response: any | undefined;
      if (type === "referral") {
        response = (await createFeedback(data)) as Feedback;
      } else {
        response = (await addAssetAppealRespondBack(data)) as Feedback;
      }
      if (response) {
        onSubmit((prev: Feedback[]) => [
          ...prev,
          response.feedback ?? response,
        ]);
        showTooltip(t("feedback_submitted_successfully"), "success");

        setResponseText(""); // reset textarea
      } else {
        showTooltip(t("feedback_not_submitted_successfully"), "error");
      }
    } catch (err) {
      console.error("Error sending feedback:", err);
      showTooltip(t("feedback_not_submitted_successfully"), "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-auto pt-4 border-t border-gray-200">
      <h3 className="font-semibold text-gray-800 mb-2">
        Write Your Feedback here
      </h3>

      <textarea
        value={responseText}
        onChange={(e) => setResponseText(e.target.value)}
        className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        rows={4}
        placeholder="write here..."
      ></textarea>

      <div className="flex justify-end mt-3">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting}
          className={`px-6 py-2 rounded-md transition-colors text-white ${
            submitting
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-black hover:bg-gray-200 "
          }`}
        >
          {submitting ? "Sending..." : "Send Feedback"}
        </button>
      </div>
    </div>
  );
}

export default FeedBackForm;
